'use client';

import { c as _c } from "react/compiler-runtime";
import { BasicTooltip } from '@/comps/basic-tooltip';
import { Skeleton } from '@/comps/primitives/skeleton';
import { PagesResizableSheet } from '@/domains/pages/shared/pages-resizable-sheet';
import { MethodologyBreakdownBar } from '@/domains/pages/ai-proficiency/components/methodology-breakdown-bar';
import { AIFI_DATA_NUMBER_CLASS } from '@/domains/pages/ai-proficiency/typography';
import { methodologyTotal, type MethodologyDimension } from '@/domains/pages/ai-proficiency/fixtures/methodology';
import { useMethodologyDrawerData } from '@/domains/pages/ai-proficiency/hooks/use-methodology-drawer-data';
import type { GetInteractionAggregatesReqFiltersV2Item } from '@/api/generated/model';
import { formatScore } from '@/utils/format.util';
import { cn } from '@/utils/shad';
import { Info } from 'lucide-react';
import { useState } from 'react';
type MethodologyDrawerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: string | undefined;
  scopeFilters?: GetInteractionAggregatesReqFiltersV2Item[];
  queryKeySuffix?: string;
};
const DIMENSION_GRID = 'grid grid-cols-[minmax(0,1fr)_40px_44px_60px] items-start gap-x-5';
const COLUMN_HEADERS = [{
  label: 'Behavior',
  tooltip: 'One of the six prompting behaviors the score is built from.',
  align: 'left'
}, {
  label: 'Score',
  tooltip: 'How this behavior scored on its own, from 0 to 100.',
  align: 'right'
}, {
  label: 'Weight',
  tooltip: 'How much this behavior counts towards the AI Proficiency score.',
  align: 'right'
}, {
  label: 'Points',
  tooltip: 'Score \u00d7 weight. The six points values add up to the AI Proficiency score.',
  align: 'right'
}] as const;
function ColumnHeader(t0) {
  const $ = _c(12);
  const {
    label,
    tooltip,
    align
  } = t0;
  const t1 = align === "right" && "justify-end";
  let t2;
  if ($[0] !== t1) {
    t2 = cn("flex items-center gap-1 whitespace-nowrap", t1);
    $[0] = t1;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <Info className="size-3" />;
    $[2] = t3;
  } else {
    t3 = $[2];
  }
  let t4;
  if ($[3] !== label) {
    t4 = <button type="button" aria-label={label} className="text-muted-foreground hover:text-foreground">{t3}</button>;
    $[3] = label;
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  let t5;
  if ($[5] !== t4 || $[6] !== tooltip) {
    t5 = <BasicTooltip text={tooltip} side="bottom" classNameContent="max-w-60 leading-relaxed break-normal">{t4}</BasicTooltip>;
    $[5] = t4;
    $[6] = tooltip;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  let t6;
  if ($[8] !== label || $[9] !== t2 || $[10] !== t5) {
    t6 = <span className={t2}>{label}{t5}</span>;
    $[8] = label;
    $[9] = t2;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
function MethodologyDrawerSkeleton() {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="space-y-4 px-6 pb-6 pt-6"><Skeleton className="h-7 w-72" /><Skeleton className="h-16 w-full" /><Skeleton className="h-3 w-full" />{Array.from({
        length: 6
      }).map(_temp)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
}
function _temp(_, index) {
  return <Skeleton key={index} className="h-12 w-full" />;
}
function MethodologyDrawerContent(t0) {
  const $ = _c(32);
  const {
    dimensions
  } = t0;
  const [hoveredKey, setHoveredKey] = useState(null);
  const breakdownScore = methodologyTotal(dimensions);
  const roundedBreakdownScore = Math.round(breakdownScore * 10) / 10;
  let t1;
  if ($[0] !== roundedBreakdownScore) {
    t1 = formatScore(roundedBreakdownScore, {
      decimals: 1
    });
    $[0] = roundedBreakdownScore;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const displayBreakdownScore = t1;
  let t2;
  let t3;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="shrink-0 pt-6"><h2 className="text-lg leading-tight font-semibold text-foreground">How the AI Proficiency score works</h2></div>;
    t3 = <p className="mt-5 shrink-0 text-sm leading-relaxed text-muted-foreground">Every interaction is scored on six dimensions of prompt quality, then combined into a single weighted score from 0 to 100. It measures how clearly people ask, not how often they use AI.</p>;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  let t4;
  if ($[4] !== displayBreakdownScore) {
    t4 = <p className="text-[10px] font-medium tracking-wider text-muted-foreground uppercase">How {displayBreakdownScore} breaks down</p>;
    $[4] = displayBreakdownScore;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== dimensions || $[7] !== hoveredKey) {
    t5 = <MethodologyBreakdownBar dimensions={dimensions} hoveredKey={hoveredKey} onHoverChange={setHoveredKey} className="mt-2" />;
    $[6] = dimensions;
    $[7] = hoveredKey;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] === Symbol.for("react.memo_cache_sentinel")) {
    t6 = <div className="mt-1 flex justify-between text-[10px] text-muted-foreground"><span>0</span><span>100</span></div>;
    $[9] = t6;
  } else {
    t6 = $[9];
  }
  let t7;
  if ($[10] !== t4 || $[11] !== t5) {
    t7 = <div className="mt-5 shrink-0">{t4}{t5}{t6}</div>;
    $[10] = t4;
    $[11] = t5;
    $[12] = t7;
  } else {
    t7 = $[12];
  }
  let t8;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = <div className={`${DIMENSION_GRID} shrink-0 pb-3 text-[10px] font-medium tracking-wider text-muted-foreground uppercase`}>{COLUMN_HEADERS.map(_temp2)}</div>;
    $[13] = t8;
  } else {
    t8 = $[13];
  }
  let t9;
  if ($[14] !== dimensions || $[15] !== hoveredKey) {
    let t10;
    if ($[17] !== hoveredKey) {
      t10 = dimension => {
        const isHovered = hoveredKey === dimension.key;
        const isDimmed = hoveredKey != null && !isHovered;
        return <div key={dimension.key} className={cn(DIMENSION_GRID, "transition-opacity", isDimmed && "opacity-35")} onMouseEnter={() => setHoveredKey(dimension.key)} onMouseLeave={() => setHoveredKey(null)}><div className="flex gap-2.5 pr-2"><span className="mt-1 size-2 shrink-0 rounded-[2px]" style={{
              backgroundColor: dimension.color
            }} /><div className="min-w-0"><p className="text-sm leading-tight font-medium text-foreground">{dimension.label}</p><p className="mt-0.5 text-xs leading-snug text-muted-foreground">{dimension.description}</p></div></div><span className={`pt-0.5 text-right text-sm font-semibold text-foreground ${AIFI_DATA_NUMBER_CLASS}`}>{formatScore(dimension.score, {
              decimals: 1
            })}</span><span className={`pt-0.5 text-right text-sm text-muted-foreground ${AIFI_DATA_NUMBER_CLASS}`}>{dimension.weight}%</span><span className={`pt-0.5 text-right text-sm font-semibold text-foreground ${AIFI_DATA_NUMBER_CLASS}`}>{dimension.points.toFixed(2)}</span></div>;
      };
      $[17] = hoveredKey;
      $[18] = t10;
    } else {
      t10 = $[18];
    }
    t9 = dimensions.map(t10);
    $[14] = dimensions;
    $[15] = hoveredKey;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[19] !== t9) {
    t10 = <div className="flex min-h-0 flex-1 flex-col justify-between overflow-hidden">{t9}</div>;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  let t12;
  let t13;
  if ($[21] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <span className="text-sm font-semibold text-foreground">AI Proficiency score</span>;
    t12 = <span />;
    t13 = <span className={`text-right text-sm text-muted-foreground ${AIFI_DATA_NUMBER_CLASS}`}>100%</span>;
    $[21] = t11;
    $[22] = t12;
    $[23] = t13;
  } else {
    t11 = $[21];
    t12 = $[22];
    t13 = $[23];
  }
  let t14;
  if ($[24] !== displayBreakdownScore) {
    t14 = <div className={`${DIMENSION_GRID} mt-4 shrink-0 border-t border-border pt-4`}>{t11}{t12}{t13}<span className={`text-right text-base leading-none font-semibold text-foreground ${AIFI_DATA_NUMBER_CLASS}`}>{displayBreakdownScore}</span></div>;
    $[24] = displayBreakdownScore;
    $[25] = t14;
  } else {
    t14 = $[25];
  }
  let t15;
  if ($[26] !== t10 || $[27] !== t14) {
    t15 = <div className="mt-6 flex min-h-0 flex-1 flex-col overflow-hidden">{t8}{t10}{t14}</div>;
    $[26] = t10;
    $[27] = t14;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  let t16;
  if ($[29] !== t15 || $[30] !== t7) {
    t16 = <div className="flex min-h-0 flex-1 flex-col overflow-hidden px-6 pb-6">{t2}{t3}{t7}{t15}</div>;
    $[29] = t15;
    $[30] = t7;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  return t16;
}
function _temp2(column) {
  return <ColumnHeader key={column.label} {...column} />;
}
export function MethodologyDrawer(t0) {
  const $ = _c(24);
  const {
    open,
    onOpenChange,
    projectId,
    scopeFilters,
    queryKeySuffix
  } = t0;
  let t1;
  if ($[0] !== open || $[1] !== projectId || $[2] !== queryKeySuffix || $[3] !== scopeFilters) {
    t1 = {
      open,
      projectId,
      scopeFilters,
      queryKeySuffix
    };
    $[0] = open;
    $[1] = projectId;
    $[2] = queryKeySuffix;
    $[3] = scopeFilters;
    $[4] = t1;
  } else {
    t1 = $[4];
  }
  const {
    dimensions,
    isLoading,
    isError
  } = useMethodologyDrawerData(t1);
  let t2;
  if ($[5] !== isLoading) {
    t2 = isLoading ? <MethodologyDrawerSkeleton /> : null;
    $[5] = isLoading;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  let t3;
  if ($[7] !== isError || $[8] !== isLoading) {
    t3 = !isLoading && isError ? <div className="px-6 py-6 text-sm text-muted-foreground">Unable to load score methodology. Close and try again.</div> : null;
    $[7] = isError;
    $[8] = isLoading;
    $[9] = t3;
  } else {
    t3 = $[9];
  }
  let t4;
  if ($[10] !== dimensions?.length || $[11] !== isError || $[12] !== isLoading) {
    t4 = !isLoading && !isError && !dimensions?.length ? <div className="px-6 py-6 text-sm text-muted-foreground">Score methodology is not available for this selection yet.</div> : null;
    $[10] = dimensions?.length;
    $[11] = isError;
    $[12] = isLoading;
    $[13] = t4;
  } else {
    t4 = $[13];
  }
  let t5;
  if ($[14] !== dimensions || $[15] !== isLoading) {
    t5 = !isLoading && dimensions?.length ? <MethodologyDrawerContent dimensions={dimensions} /> : null;
    $[14] = dimensions;
    $[15] = isLoading;
    $[16] = t5;
  } else {
    t5 = $[16];
  }
  let t6;
  if ($[17] !== onOpenChange || $[18] !== open || $[19] !== t2 || $[20] !== t3 || $[21] !== t4 || $[22] !== t5) {
    t6 = <PagesResizableSheet open={open} onOpenChange={onOpenChange}>{t2}{t3}{t4}{t5}</PagesResizableSheet>;
    $[17] = onOpenChange;
    $[18] = open;
    $[19] = t2;
    $[20] = t3;
    $[21] = t4;
    $[22] = t5;
    $[23] = t6;
  } else {
    t6 = $[23];
  }
  return t6;
}